import { useMemo, useState } from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Graph_QL, useSystem, useUser } from "../../Services";
import { useAppSelector } from "../../Store";
import { ButtonTypes, ListData_ValueTypes, TableStyles_Types } from "../../Definitions";
import { useColumnConstructor } from "../Tables/_Tables/useColumnConstructor";
import { NewButton } from "../BaseComponents";
import { colors, fonts, glyphs } from "../../Styles";
import { ReactiveTable } from "../Tables/reactiveTableRenderer";
import WorkerListModal from "./Modals/workerListModal";

export function WorkerList() {
  const { t } = useTranslation();
  const { workerList } = useUser();
  const [selectedWorkerId, setSelectedWorkerId] = useState<number | undefined>();

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({ domain: farm })), [farm]);
  const connection = useMemo(() => (Graph_QL.create({ domain: farm })), [farm]);

  const { data: systemsState } = useSystem(connection);
  
  const systems = useMemo(() => {
    return systemsState?.[domain]?.data || [];
  }, [domain, systemsState]);

  const selectedWorker = useMemo(() => workerList?.find(worker => worker.user_id === selectedWorkerId), [workerList, selectedWorkerId]);

  const accessorData = useMemo(() =>[
    { headerValue: t("user.userName", { defaultValue: "username" }), accessor: "action", type: ListData_ValueTypes.String },
    { headerValue: t("globalSettingsPages.globalSettings.systemNotifications", {defaultValue: "system notifications"}), accessor: "systemNotifikations", type: ListData_ValueTypes.String }
  ], [t]);

  const columns = useColumnConstructor({
    accessorData: [...accessorData],
    tableStyle: TableStyles_Types.lightTable,
    canEdit: false,
  });

  const tableData = useMemo(() => {
    return workerList?.map((e) => ({
      id: e.user_id,
      action: <NewButton
        key={e.user_id}
        content={e.username}
        buttonType={ButtonTypes.textButton}
        startEnhancer={ glyphs.ArrowSmooth({fillColor: colors.greyDarkSecondary})}
        onClick={() => setSelectedWorkerId(e.user_id)}
        overrides={{
          contentStyles: {...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary},
          startEnhancerStyles: { margin: "0px 0px 2px 0px" }
        }}
      />,
      systemNotifikations: systems.filter(s => e.attention_email_systems?.includes(s.system_id)).map(s => s.name).join(", ") ?? ""
    })) ?? [];
  }, [systems, workerList]);

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
  });

  function closeModal() {
    setSelectedWorkerId(undefined);
  }

  return ( <>
      <ReactiveTable
        table={table}
        tableType={TableStyles_Types.lightTable}
        emptyText={"No users to see"} //TODO TRANSLATE
        renderFooter={false}
      />
      {selectedWorker && <WorkerListModal worker={selectedWorker} closeModal={closeModal} />}
    </> );
}